import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MessageSquare, Send } from "lucide-react";

const ROLE_BADGE = {
  admin: "bg-red-100 text-red-700 border-red-200",
  accountant: "bg-purple-100 text-purple-700 border-purple-200",
  ibo: "bg-blue-100 text-blue-700 border-blue-200",
  sales: "bg-indigo-100 text-indigo-700 border-indigo-200",
  user: "bg-gray-100 text-gray-700 border-gray-200",
};

const ROLE_LABEL = {
  admin: "Admin", accountant: "Accountant", ibo: "IBO",
  sales: "BDM", user: "User",
};

/**
 * Chat panel for a single B2B submission. Reads + posts to
 * /api/b2b/submissions/{id}/messages.
 *
 * Usable at any bill stage (Pending / Approved / Rejected / etc.). Access
 * is gated server-side so the same component works for admin, accountant,
 * and the IBO who submitted the bill.
 */
export default function B2BChat({ submissionId, currentUserId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);

  const load = useCallback(async () => {
    if (!submissionId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/b2b/submissions/${submissionId}/messages`);
      setMessages(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [submissionId]);

  useEffect(() => { load(); }, [load]);

  // Auto-scroll to the bottom whenever messages update
  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async () => {
    const t = draft.trim();
    if (!t) return;
    if (t.length < 1) return;
    setSending(true);
    try {
      const { data } = await api.post(
        `/b2b/submissions/${submissionId}/messages`,
        { message: t },
      );
      setMessages((cur) => [...cur, data]);
      setDraft("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border rounded-md" data-testid="b2b-chat">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        <MessageSquare className="w-4 h-4 text-muted-foreground" />
        <span className="text-xs font-medium">Bill conversation</span>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {loading ? "loading…" : `${messages.length} message${messages.length === 1 ? "" : "s"}`}
        </span>
      </div>
      <div
        ref={listRef}
        className="max-h-72 overflow-y-auto p-3 space-y-2 bg-white"
        data-testid="b2b-chat-list"
      >
        {messages.length === 0 && !loading && (
          <div className="text-xs text-muted-foreground text-center py-6">
            No messages yet. Start the conversation.
          </div>
        )}
        {messages.map((m, idx) => {
          const isMe = m.sender_id === currentUserId;
          const badgeCls = ROLE_BADGE[m.sender_role] || ROLE_BADGE.user;
          const roleLabel = ROLE_LABEL[m.sender_role] || m.sender_role || "User";
          return (
            <div
              key={m.id || idx}
              className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}
              data-testid={`b2b-chat-msg-${idx}`}
            >
              <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground mb-0.5">
                <span className={`inline-block border rounded-full px-1.5 py-0.5 font-medium ${badgeCls}`}>{roleLabel}</span>
                <span>{m.sender_name || "—"}</span>
                <span>·</span>
                <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ""}</span>
              </div>
              <div
                className={`max-w-[85%] rounded-lg px-3 py-1.5 text-sm whitespace-pre-wrap break-words ${
                  isMe ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                }`}
              >
                {m.message}
              </div>
            </div>
          );
        })}
      </div>
      <div className="border-t p-2 flex items-end gap-2 bg-muted/20">
        <Textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type your message and press Enter to send…"
          className="text-sm resize-none"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              if (!sending && draft.trim()) send();
            }
          }}
          data-testid="b2b-chat-input"
        />
        <Button
          type="button"
          onClick={send}
          disabled={sending || !draft.trim()}
          size="sm"
          data-testid="b2b-chat-send"
        >
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
